import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import usePageTitle from '../../hooks/usePageTitle';
import DashboardShell from "../../components/Dashboard/DashboardShell";
import NewEpkBtn from "../../components/Dashboard/Epks/NewEpkBtn";
import EpkCard from "../../components/Dashboard/Epks/EpkCard";
import ArchivedEpkCard from "../../components/Dashboard/Epks/ArchivedEpkCard";
import { getFepksByFilmmakerId, getDeletedFepksByFilmmakerId } from "../../api/epks";
import EmptyEpk from "../../components/Dashboard/Epks/EmptyEpk";
import LoadingSpin from "../../components/Dashboard/LoadingSpin";
import { useTranslation } from "react-i18next";
import EpkTextInfo from "../../components/Dashboard/Epks/EpkTextInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faTrash, faSearch } from "@fortawesome/free-solid-svg-icons";

export default function EpkPage() {
  usePageTitle('My EPKs');
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const [epkList, setEpkList] = useState([]);
  const [deletedEpkList, setDeletedEpkList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeleted, setLoadingDeleted] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    getFepksByFilmmakerId(user.id).then((res) => {
      setEpkList(res);
      setLoading(false);
    });

    getDeletedFepksByFilmmakerId(user.id).then((res) => {
      setDeletedEpkList(res);
      setLoadingDeleted(false);
    });
  }, [user.id]);

  const handleDelete = (deletedId) => {
    const epk = epkList.find((e) => e._id === deletedId);
    setEpkList((prev) => prev.filter((e) => e._id !== deletedId));
    if (epk) {
      setDeletedEpkList((prev) => [{ ...epk, deleted: true }, ...prev]);
      setShowDeleted(true);
    }
  };

  const handleRestore = (restoredId) => {
    const epk = deletedEpkList.find((e) => e._id === restoredId);
    setDeletedEpkList((prev) => prev.filter((e) => e._id !== restoredId));
    if (epk) setEpkList((prev) => [{ ...epk, deleted: false }, ...prev]);
  };

  return (
    <DashboardShell role={user.role} pageTitle={t("EPKs")}>
      <div className="tw-flex tw-flex-col tw-gap-8">
        {loading ? (
          <LoadingSpin />
        ) : epkList.length === 0 ? (
          <EmptyEpk />
        ) : (
          <>
            <EpkTextInfo />
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-relative tw-flex-1">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/30 tw-text-sm tw-pointer-events-none"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("Search EPKs by title...")}
                  className="tw-w-full tw-rounded-xl tw-border tw-border-white/20 tw-bg-[#190033] tw-pl-9 tw-pr-4 tw-py-2.5 tw-text-sm tw-text-white placeholder:tw-text-white/30 focus:tw-border-[#712CB0] focus:tw-outline-none tw-transition-colors"
                />
              </div>
              <NewEpkBtn />
            </div>
            {(() => {
              const filtered = epkList.filter((e) =>
                e.title?.toLowerCase().includes(search.toLowerCase())
              );
              return filtered.length > 0 ? (
                <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-justify-items-start">
                  {filtered.map((epk) => (
                    <EpkCard key={epk._id} EpkInfo={epk} onDelete={handleDelete} />
                  ))}
                </div>
              ) : (
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-12">
                  <p className="tw-text-white/40 tw-text-sm tw-m-0">
                    {t("No EPKs match")} <span className="tw-text-white/60 tw-font-medium">"{search}"</span>
                  </p>
                </div>
              );
            })()}
          </>
        )}

        {!loadingDeleted && deletedEpkList.length > 0 && (
          <div className="tw-border-t tw-border-white/10 tw-pt-6">
            <button
              onClick={() => setShowDeleted((prev) => !prev)}
              className="tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-bg-white/5 tw-px-4 tw-py-3 tw-text-left tw-text-white/60 hover:tw-bg-white/10 tw-transition-colors tw-border-0 tw-cursor-pointer"
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <FontAwesomeIcon icon={faTrash} className="tw-text-white/40 tw-text-sm" />
                <span className="tw-font-semibold tw-text-sm">
                  {t("Deleted EPKs")}
                  <span className="tw-ml-2 tw-rounded-full tw-bg-white/10 tw-px-2 tw-py-0.5 tw-text-xs tw-text-white/60">
                    {deletedEpkList.length}
                  </span>
                </span>
              </div>
              <FontAwesomeIcon
                icon={showDeleted ? faChevronUp : faChevronDown}
                className="tw-text-white/40 tw-text-sm"
              />
            </button>

            {showDeleted && (
              <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-justify-items-start">
                {deletedEpkList.map((epk) => (
                  <ArchivedEpkCard key={epk._id} EpkInfo={epk} onRestore={handleRestore} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
